import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ExternalLink, Loader2, Search } from "lucide-react";
import { channelVideos, channelVideosSeen } from "../lib/api";
import { fmtTime } from "./tv/ChannelVideos";

const day = (s) => new Date(s).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const Calls = ({ rows, tone }) => rows?.length ? (
  <div className="flex flex-wrap gap-1.5">{rows.map((p, i) => (
    <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${tone}`} title={p.reason}><b>{p.ticker}</b> {p.call} · {p.reason}</span>))}</div>
) : null;

const Row = ({ v, onOpen }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => { setOpen((o) => !o); if (!v.read) onOpen(v.video_id); };
  return (
    <div className="rounded-xl border border-cyan-300/10 bg-white/[0.03]" data-testid={`videos-row-${v.video_id}`}>
      <button onClick={toggle} className="w-full text-left p-3 flex gap-3 items-start" data-testid={`videos-row-toggle-${v.video_id}`}>
        {!v.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#00F0FF] shrink-0" data-testid="videos-unread-dot" />}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] nx-mono text-cyan-300/60">{v.channel} · {fmtTime(v.scraped_at)} · <span className="text-violet-300">{v.topic}</span></p>
          <p className="text-sm font-bold text-cyan-50 leading-snug">{v.title}</p>
          <p className="text-xs text-cyan-100/60 truncate">{(v.summary || [])[0]}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-cyan-300/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 text-xs text-cyan-100/80" data-testid={`videos-row-detail-${v.video_id}`}>
          <ul className="list-disc pl-4 space-y-1">{(v.summary || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
          {v.facts?.length > 0 && <div><p className="nx-mono text-[10px] tracking-widest text-cyan-300/60 mb-1">KEY FACTS</p><ul className="list-disc pl-4 space-y-1">{v.facts.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          <Calls rows={v.picks} tone="border-emerald-400/40 text-emerald-200" />
          <Calls rows={v.rejects} tone="border-rose-400/40 text-rose-200" />
          <a href={v.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#00F0FF]" data-testid="videos-row-link"><ExternalLink className="w-3.5 h-3.5" /> Watch on YouTube</a>
          <div><p className="nx-mono text-[10px] tracking-widest text-cyan-300/60 mb-1">TRANSCRIPT {v.transcript_source ? `· ${v.transcript_source}` : ""}</p>
            <p className="max-h-72 overflow-y-auto whitespace-pre-wrap leading-relaxed bg-black/30 rounded-lg p-2" data-testid="videos-row-transcript">{v.transcript}</p></div>
        </div>
      )}
    </div>
  );
};

export const ChannelVideosTab = ({ onUnread }) => {
  const [items, setItems] = useState(null);
  const [chans, setChans] = useState([]);
  const [ch, setCh] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => channelVideos({ channel: ch, q, limit: 100 }).then((d) => { setItems(d.items); setChans(d.channels); }).catch(() => setItems([])), [ch, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { onUnread && items && onUnread(items.filter((v) => !v.read).length); }, [items, onUnread]);
  const open = (id) => { channelVideosSeen([id], "read").catch(() => {}); setItems((xs) => xs.map((v) => (v.video_id === id ? { ...v, read: true } : v))); };
  const groups = {};
  (items || []).forEach((v) => { (groups[day(v.scraped_at)] = groups[day(v.scraped_at)] || []).push(v); });
  return (
    <div className="h-full overflow-y-auto px-4 py-4 space-y-3" data-testid="videos-tab">
      <div className="flex gap-2">
        <select value={ch} onChange={(e) => setCh(e.target.value)} data-testid="videos-channel-filter" className="bg-white/5 border border-cyan-300/15 rounded-xl px-2 text-sm text-cyan-50">
          <option value="">All channels</option>{chans.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex-1 flex items-center gap-2 bg-white/5 border border-cyan-300/15 rounded-xl px-3">
          <Search className="w-4 h-4 text-cyan-300/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles and transcripts" data-testid="videos-search" className="flex-1 bg-transparent py-2 text-sm outline-none" />
        </div>
      </div>
      {!items ? <Loader2 className="w-5 h-5 animate-spin text-cyan-400 mx-auto" /> : items.length === 0
        ? <p className="text-sm text-cyan-100/50 text-center pt-8" data-testid="videos-empty">No videos yet — the scraper runs daily at 4:00 AM CT.</p>
        : Object.entries(groups).map(([g, vs]) => (
          <div key={g} className="space-y-2"><p className="nx-mono text-[11px] tracking-widest text-cyan-300/60 pt-1">{g.toUpperCase()}</p>
            {vs.map((v) => <Row key={v.video_id} v={v} onOpen={open} />)}</div>))}
    </div>
  );
};
